import React, { useState } from 'react';
import Link from 'next/link';
import styled from '@emotion/styled';

// 다국어 지원
import { useTranslation } from 'next-i18next';

// colors
import { gray } from '@styles/Colors';

// components
import TextInput from '@components/_atoms/TextInput';
import Button from '@components/_atoms/Button';
import Typography from '@components/_atoms/Typography';

// constants
import { TypoVariant } from '@constants/atoms';
import { HOME, SIGNUP, FIND, needPath, REGACY } from '@constants/routes/routes';
import { useLoginFetch } from '@hooks/fetch/useAuthFetch';
import { useRouter } from 'next/router';

// type
import { LoginFormType } from './types';
import { returnRemoveWhiteSpace } from '@utils/regex';
import { ACCESS_TOKEN, REFRESH_TOKEN, removeCookie } from '@utils/cookie';
import { useRecoilState, useRecoilValue } from 'recoil';
import { previousPageUrlState } from '@recoil/auth';
import ButtonCode from '@constants/log/buttonCode';
import useLogin from '@hooks/fetch/spring/auth/useLogin';

const LoginFormWrapper = styled.form`
  width: 100%;
`;

const InputWrapper = styled.div`
  display: grid;
  row-gap: 16px;
  margin-bottom: 32px;
`;

const TextWrapper = styled.div`
  margin-top: 18px;
  display: flex;
  justify-content: space-between;
`;

export default function LoginContent() {
  const router = useRouter();
  const { t } = useTranslation('auth');

  const [form, setForm] = useState<LoginFormType>({ email: '', password: '' });
  const [previousPageUrl, setPreviousPageUrl] = useRecoilState(previousPageUrlState);

  const { mutate: loginFetchMutate } = useLogin();

  // form change
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: returnRemoveWhiteSpace(value).trim(),
    }));
  };

  // form submit
  const handleFormSubmit = () => {
    loginFetchMutate(form, {
      // login success
      onSuccess: (response) => {
        const { code } = response;

        // v2 회원
        if (code === 2008) {
          router.replace(REGACY);
          return;
        }
        if (previousPageUrl) {
          setPreviousPageUrl(undefined);

          router.replace(previousPageUrl);
        } else {
          router.replace(HOME);
        }
      },
      onError: (error) => {
        const { code } = error.response?.data || {};

        // 이메일 인증 X
        if (code === 2001) {
          const NEED = needPath.query({ email: form.email });
          router.push(NEED);
        }

        const currentDomain = window.location.hostname;

        // 로그인 실패시 Auth토큰 삭제 (클라이언트 코드상의 토큰은 유효하지만, 서버리셋과같은 상황에선 토큰이 invali할때 클라이언트상의 Auth토큰을 지우는 로직)
        removeCookie(ACCESS_TOKEN, { domain: currentDomain, path: '/' });
        removeCookie(REFRESH_TOKEN, { domain: currentDomain, path: '/' });
      },
    });
  };

  return (
    <LoginFormWrapper
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit();
      }}>
      <InputWrapper>
        <TextInput
          placeholder={t('login.LoginForm.placeholder1')}
          name="email"
          onChange={handleFormChange}
          value={form.email}
        />
        <TextInput
          placeholder={t('login.LoginForm.placeholder2')}
          type="password"
          name="password"
          onChange={handleFormChange}
          value={form.password}
        />
      </InputWrapper>
      <Button width="100%" text={t('login.LoginForm.submit-button')} steelbosoId={ButtonCode.BTN_SUBMIT} />
      <TextWrapper>
        <Link href={FIND}>
          <a data-steelboso-id={ButtonCode.BTN_FIND_USER}>
            <Typography variant={TypoVariant.SH4} color={gray.gray6}>
              {t('login.LoginForm.find-link')}
            </Typography>
          </a>
        </Link>
        <Link href={SIGNUP}>
          <a data-steelboso-id={ButtonCode.BTN_JOIN}>
            <Typography variant={TypoVariant.SH4} color={gray.gray6}>
              {t('login.LoginForm.signup-link')}
            </Typography>
          </a>
        </Link>
      </TextWrapper>
    </LoginFormWrapper>
  );
}
